// The inline order-detail panel under a row of the new BO order list.
// Spec: docs/specs/2026-10-07 (bo orders_new status tabs design) §14.4 (R27–R30),
// §15.3 (R32–R35: no "hide images" switch any more, in-place payment check).
//
// Pure helpers only (no vue import): tests/unit/orderDetailPanel.spec.js drives
// them directly. Nothing here throws — a bad summary string or a missing field
// fall back to "nothing to show".

/** Path prefixes of the BO API file server (§14.2). */
export const IMAGE_PREFIX = {
  product: '/files/products/',
  wms: '/files/wms_products/',
  slip: '/files/orders/'
};

/**
 * imageUrl(base, prefix, value) → a URL for `:src`, or "" when there is none.
 *  - empty / whitespace / null → ""
 *  - comma-separated list → the first non-empty entry
 *  - an entry starting with "http" is already absolute → returned as is
 *  - otherwise base + prefix + filename
 */
export function imageUrl(base, prefix, value) {
  if (value === null || value === undefined) return '';
  const first = String(value)
    .split(',')
    .map(s => s.trim())
    .find(s => s !== '');
  if (!first) return '';
  if (/^https?:\/\//i.test(first)) return first;
  return String(base || '') + String(prefix || '') + first;
}

function toItem(x) {
  if (!x || typeof x !== 'object') return null;
  return {
    wms_product_id: x.wms_product_id || 0,
    wms_product_name: x.wms_product_name || '',
    barcode: x.barcode || '',
    pickup_quantity: Number(x.pickup_quantity) || 0,
    image: x.image || ''
  };
}

/**
 * parseWmsSummary(str) → [{ wms_product_id, wms_product_name, barcode,
 * pickup_quantity, image: "" }]. `wms_product_summary` is a JSON string of an
 * array; anything else (empty, invalid JSON, not an array) → [].
 * Fallback for an API that does not send `wms_items` yet (§14.4 R29).
 */
export function parseWmsSummary(str) {
  if (Array.isArray(str)) return str.map(toItem).filter(Boolean);
  if (typeof str !== 'string' || str.trim() === '') return [];
  let parsed;
  try {
    parsed = JSON.parse(str);
  } catch (e) {
    return [];
  }
  return Array.isArray(parsed) ? parsed.map(toItem).filter(Boolean) : [];
}

/** WMS pack list: detail.wms_items (2.8.278+, has images) else the row's summary. */
export function wmsItemsOf(detail, row) {
  if (detail && Array.isArray(detail.wms_items)) return detail.wms_items.map(toItem).filter(Boolean);
  const summary = (detail && detail.wms_product_summary) || (row && row.wms_product_summary);
  return parseWmsSummary(summary);
}

/** Order items of the detail response ([] when absent). */
export function orderItemsOf(detail) {
  return detail && Array.isArray(detail.order_items) ? detail.order_items : [];
}

/** Status code of "awaiting payment" (รอชำระเงิน). */
export const STATUS_AWAITING_PAYMENT = 100;

/**
 * §17 R39: only orders of platform 1 (PlatformSelf, "มาดามฟิน") can be payment
 * checked in the BO. 9 = MDF Store and the marketplaces are NOT included.
 */
export const PAYMENT_CHECK_PLATFORM_IDS = [1];

/**
 * §15.3 R35 + §17 R39: the "payment correct / incorrect" buttons show for a
 * platform-1 order that awaits payment, to a user with isAccessPayment only
 * (the BE gate is mw.IsFullPayment — isAdmin alone is not enough).
 */
export function canCheckPayment(row, roles) {
  return !!row
    && PAYMENT_CHECK_PLATFORM_IDS.indexOf(Number(row.platform_id)) !== -1
    && Number(row.order_status) === STATUS_AWAITING_PAYMENT
    && !!(roles && roles.isAccessPayment);
}

/**
 * §15.3 R35: the right column exists when there is a slip, or when the payment
 * buttons must show (status 100 + permission) even without a slip.
 */
export function hasRightColumn(row, roles) {
  const hasSlip = imageUrl('', '', row && row.payment_files) !== '';
  return hasSlip || canCheckPayment(row, roles);
}

/** OrderService method per payment-check kind; null for anything else. */
const PAYMENT_CHECK_CALLS = {
  success: 'OrderPaymentConfirmSetStatusToAwaitingShipment',
  fail: 'OrderPaymentConfirmSetStatusToCancelled'
};
export function paymentCheckCall(kind) {
  return Object.prototype.hasOwnProperty.call(PAYMENT_CHECK_CALLS, kind) ? PAYMENT_CHECK_CALLS[kind] : null;
}

/** Column classes: info left / right column (slip, payment check) when there is one, else full width. */
export function detailLayout(hasRight) {
  return hasRight
    ? { main: 'col-12 col-xl-8', slip: 'col-12 col-xl-4' }
    : { main: 'col-12', slip: '' };
}

// --------------------------------------------------------------------------
// §19 R44 (round 11): packing open/close info in the "จัดส่ง" block. Same
// fields as the old page (OrderTables.vue "ผู้เปิด Order แพ็ค" / เวลาเปิด /
// เวลาปิด / "สายที่แพ็ค"). No closer: bo_orders has no such column and the
// owner ruled out a new one (2026-10-08).
// --------------------------------------------------------------------------

const EMPTY = '-';

function blank(value) {
  if (value === null || value === undefined) return true;
  return String(value).trim() === '';
}

/** A timestamp string as the page shows it (raw, like created_at); blank / zero date -> "". */
function timeText(value) {
  if (blank(value)) return '';
  const s = String(value).trim();
  return /^(0001-01-01|0000-00-00)/.test(s) ? '' : s;
}

/**
 * storageName(id, storages) — the old page's GetStorageName: the `Name` of
 * the WmsStorage row whose `ID` matches; no id (0 / blank) or no match -> "-".
 */
export function storageName(id, storages) {
  if (blank(id) || Number(id) === 0 || Number.isNaN(Number(id))) return EMPTY;
  const list = Array.isArray(storages) ? storages : [];
  const hit = list.find(x => x && Number(x.ID) === Number(id));
  return hit && !blank(hit.Name) ? String(hit.Name) : EMPTY;
}

/**
 * packingInfo(order, storages) -> { openedBy, openedAt, closedAt, line } —
 * every field a display string, "-" when empty. Never throws.
 */
export function packingInfo(order, storages) {
  const o = order && typeof order === 'object' ? order : {};
  return {
    openedBy: blank(o.packing_opened_user_name) ? EMPTY : String(o.packing_opened_user_name).trim(),
    openedAt: timeText(o.packing_opened_at) || EMPTY,
    closedAt: timeText(o.packing_closed_at) || EMPTY,
    line: storageName(o.packing_opened_storage_id, storages)
  };
}
